import { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ExternalLink, ChevronLeft, ChevronRight, Rotate3d } from "lucide-react";
import { useTours } from "@/data/useTours";

const PAGE_SIZE = 21;

export const embedFor = (t) =>
  `https://www.google.com/maps/embed?pb=!4v1787317843452!6m8!1m7!1s${t.pano}!2m2!1d${t.lat}!2d${t.lng}!3f0!4f0!5f0.7820865974627469`;

export const TourModal = ({ tour, embedUrl, title, mapsUrl, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="tour-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-navy-deep/90 backdrop-blur-md"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`360° view of ${title}`}
    >
      <motion.div
        initial={{ scale: 0.94, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.94, y: 20 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-4xl bg-navy-light border border-white/10 rounded-3xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="font-display font-bold text-mist text-lg truncate pr-4">{title}</h3>
          <button onClick={onClose} data-testid="tour-modal-close" aria-label="Close 360° view" className="text-mist/60 hover:text-electric transition-colors p-1">
            <X size={22} />
          </button>
        </div>
        <iframe
          src={embedUrl}
          title={`360° virtual tour — ${title}`}
          className="w-full aspect-[4/3] sm:aspect-video border-0"
          allowFullScreen
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
        />
        <div className="px-6 py-4 flex items-center justify-between gap-4">
          <span className="text-slatec text-sm flex items-center gap-2">
            <Rotate3d size={16} className="text-electric" />
            Drag inside the view to look around
          </span>
          {mapsUrl && (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tour-open-maps"
              className="inline-flex items-center gap-1.5 text-electric text-sm font-semibold shrink-0"
            >
              Open in Google Maps <ExternalLink size={14} />
            </a>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const TourExplorer = () => {
  const tours = useTours();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [activeTour, setActiveTour] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tours;
    return tours.filter((t) => t.title.toLowerCase().includes(q));
  }, [query, tours]);

  const pages = Math.ceil(filtered.length / PAGE_SIZE);
  const visible = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  useEffect(() => setPage(0), [query]);

  return (
    <div data-testid="tour-explorer">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between mb-10">
        <p className="text-slatec text-sm" data-testid="tour-count">
          <span className="text-electric font-semibold">{filtered.length}</span> published 360° experiences
        </p>
        <div className="relative w-full sm:w-80">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-mist/40" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tours…"
            data-testid="tour-search-input"
            aria-label="Search 360° tours"
            className="w-full bg-white/5 border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm text-mist placeholder:text-mist/30 focus:border-electric/50 outline-none transition-colors"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {visible.map((t, i) => (
          <motion.button
            key={`${t.pano}-${i}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
            onClick={() => setActiveTour(t)}
            data-testid={`tour-card-${i}`}
            className="group relative text-left rounded-2xl overflow-hidden border border-white/10 bg-navy-light hover:border-electric/40 transition-colors duration-300"
          >
            <div className="relative aspect-[2/1] overflow-hidden">
              <img
                src={t.thumb}
                alt={`360° tour thumbnail — ${t.title}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-navy-deep/30 group-hover:bg-navy-deep/10 transition-colors duration-300" />
              <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-navy-deep/70 backdrop-blur flex items-center justify-center text-electric opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Rotate3d size={16} />
              </span>
            </div>
            <div className="px-5 py-4">
              <h3 className="font-display font-bold text-mist text-base leading-snug line-clamp-2">{t.title}</h3>
            </div>
          </motion.button>
        ))}
      </div>

      {pages > 1 && (
        <div className="mt-12 flex items-center justify-center gap-4" data-testid="tour-pagination">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            data-testid="tour-prev-page"
            aria-label="Previous page"
            className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-mist disabled:opacity-30 hover:border-electric hover:text-electric transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm text-slatec" data-testid="tour-page-indicator">
            Page {page + 1} of {pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
            disabled={page >= pages - 1}
            data-testid="tour-next-page"
            aria-label="Next page"
            className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-mist disabled:opacity-30 hover:border-electric hover:text-electric transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      <AnimatePresence>
        {activeTour && (
          <TourModal
            tour={activeTour}
            title={activeTour.title}
            embedUrl={embedFor(activeTour)}
            mapsUrl={activeTour.url}
            onClose={() => setActiveTour(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default TourExplorer;
